/**
 * ObjectivesManagement Component
 *
 * Main screen for managing the trading objectives library.
 * Provides access to queued objectives and full library.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { ColDef, RowClassParams } from 'ag-grid-community';

// 2. Internal absolute imports
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  AGGridTable,
  Chip,
  EmptyState,
  Button,
  NumberInput,
  useDisclosure,
  useToastNotification,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Input,
  Select,
  SelectItem,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip,
  SortableList,
  DragHandle,
} from '@enfomentis/ui';
import type { DragHandleProps } from '@enfomentis/ui';
import { Target, Plus, Filter, Search, MoreVertical, Trash2, AlertTriangle, ListPlus, X, ChevronDown, ChevronUp, Maximize2, Minimize2 } from 'lucide-react';

// 5. Local imports
import { ObjectiveModal } from './ObjectiveModal';
import { useLiveSettings } from '../PreTrading/hooks/useLiveSettings';

// 3. Utils
import { formatDate, handleFilterSelection } from '@/components/shared/InstrumentExplorer/utils';

// 4. Services & Types
import { tradingObjectiveService } from '@/services/TradingObjectiveService';
import { tradingObjectiveSettingsService } from '@/services/TradingObjectiveSettingsService';
import { preTradingSettingsService } from '@/services/PreTradingSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { ObjectiveType } from '@/types/schema/TradingObjectiveSettings';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import type { PreTradingSettings, QueuedObjective } from '@/types/schema/PreTradingSettings';

// Constant to avoid collision with UUIDs
const FILTER_ALL = 'all';

/**
 * Status cell renderer - displays status as colored chip
 */
const StatusRenderer: React.FC<{ data: TradingObjective }> = ({ data }) => {
  const statusConfig: Record<EntityStatus, { color: 'success' | 'default' | 'warning'; label: string }> = {
    active: { color: 'success', label: 'Active' },
    inactive: { color: 'default', label: 'Inactive' },
    archived: { color: 'warning', label: 'Archived' }
  };

  const config = statusConfig[data.status];

  return (
    <Chip
      color={config.color}
      size="sm"
      variant="flat"
      className={data.status !== 'active' ? 'opacity-60' : ''}
    >
      {config.label}
    </Chip>
  );
};

/**
 * Tags cell renderer - displays tags as array of chips
 */
const TagsRenderer: React.FC<{ data: TradingObjective }> = ({ data }) => {
  if (!data.tags || data.tags.length === 0) {
    return <span className="text-text-tertiary text-sm">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1 items-center h-full">
      {data.tags.slice(0, 3).map((tag, index) => (
        <Chip key={index} size="sm" variant="flat" color="default">
          {tag}
        </Chip>
      ))}
      {data.tags.length > 3 && (
        <Chip size="sm" variant="flat" color="default">
          +{data.tags.length - 3}
        </Chip>
      )}
    </div>
  );
};

/**
 * Name cell renderer props
 */
interface NameRendererProps {
  data?: TradingObjective;
  queuedIdSet?: Set<string>;
}

/**
 * Name cell renderer - displays name with queue indicator
 */
const NameRenderer: React.FC<NameRendererProps> = ({ data, queuedIdSet }) => {
  if (!data) return null;

  const isQueued = queuedIdSet?.has(data.id) ?? false;

  return (
    <div className="flex items-center gap-2 h-full">
      <span className="font-medium">{data.name}</span>
      {isQueued && (
        <Tooltip content="Queued for session">
          <ListPlus size={16} className="text-primary shrink-0" />
        </Tooltip>
      )}
    </div>
  );
};

/**
 * Actions cell renderer props
 */
interface ActionsRendererProps {
  data?: TradingObjective;
  onEditClick?: (objective: TradingObjective) => void;
  onStatusChange?: (objective: TradingObjective, newStatus: EntityStatus) => void;
  onDeleteClick?: (objective: TradingObjective) => void;
  onQueueClick?: (objective: TradingObjective) => void;
  /** Disable queue action while objectives are loading */
  isQueueDisabled?: boolean;
}

/**
 * Adapter type for SortableList (requires `id` property)
 */
interface SortableQueueItem {
  id: string;  // Maps to objectiveId
  queuedObjective: QueuedObjective;
  objective: TradingObjective;  // Always valid (derived from validQueue)
}

/**
 * QueuedItemRenderer props
 */
interface QueuedItemRendererProps {
  item: SortableQueueItem;
  dragHandleProps: DragHandleProps;
  isFirst: boolean;
  onRemove: (objectiveId: string) => void;
  getTypeName: (typeId: string) => string;
}

/**
 * Renders a single queued objective item with drag handle and remove button
 */
const QueuedItemRenderer: React.FC<QueuedItemRendererProps> = ({
  item,
  dragHandleProps,
  isFirst,
  onRemove,
  getTypeName,
}) => {
  const { objective } = item;
  const typeName = objective.type ? getTypeName(objective.type) : null;
  // const tags = objective.tags ?? [];

  return (
    <Card
      shadow="sm"
      className={`${isFirst ? 'ring-1 ring-primary' : ''}`}
      radius="md"
    >
      <CardBody className="flex flex-row items-center gap-3 py-2 px-3">
        <DragHandle dragHandleProps={dragHandleProps} className="shrink-0" />

        <div className="flex-1 min-w-0">
          {/* Row 1: Name • Type • Description (desktop) • Primary */}
          <div className="flex flex-wrap items-center gap-2">
            <span className=" text-text-primary truncate">
              {objective.name}
            </span>
            {typeName && (
              <>
                <span className="text-text-primary">•</span>
                <span className="uppercase text-sm text-text-primary truncate">{typeName}</span>
              </>
            )}
            {objective.description && (
              <>
                <span className="text-text-primary hidden md:inline">•</span>
                <span className="text-sm text-text-secondary truncate hidden md:inline">
                  {objective.description}
                </span>
              </>
            )}
            {isFirst && (
              <>
                {/* <span className="text-text-primary">•</span> */}
                <Chip size="sm" color="primary" variant="flat">
                  Primary
                </Chip>
              </>
            )}
          </div>
          {/* Row 2: Tags (both mobile and desktop) */}
          {/* {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {tags.slice(0, 3).map((tag, index) => (
                <Chip key={index} size="sm" variant="flat" color="default">
                  {tag}
                </Chip>
              ))}
              {tags.length > 3 && (
                <Chip size="sm" variant="flat" color="default">
                  +{tags.length - 3}
                </Chip>
              )}
            </div>
          )} */}
        </div>

        <Button
          isIconOnly
          size="sm"
          variant="light"
          color="danger"
          onPress={() => onRemove(objective.id)}
          aria-label={`Remove ${objective.name} from queue`}
        >
          <X size={16} />
        </Button>
      </CardBody>
    </Card>
  );
};

/**
 * Actions cell renderer - dropdown menu with queue, edit, status, and delete options
 */
const ActionsRenderer: React.FC<ActionsRendererProps> = ({ data, onEditClick, onStatusChange, onDeleteClick, onQueueClick, isQueueDisabled }) => {
  if (!data) return null;

  // Check if objective can be deleted (never used in sessions)
  const canDelete = (data.usageHistory?.length ?? 0) === 0;

  // Build disabled keys array
  const disabledKeys: string[] = [];
  if (!canDelete) disabledKeys.push('delete');
  if (isQueueDisabled) disabledKeys.push('queue');

  return (
    <div className="flex items-center justify-center h-full">
      <Dropdown>
        <DropdownTrigger>
          <Button
            size="sm"
            variant="light"
            isIconOnly
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Objective actions"
          disabledKeys={disabledKeys}
        >
          <DropdownItem
            key="queue"
            startContent={<ListPlus size={16} />}
            onPress={() => onQueueClick?.(data)}
          >
            Queue for Session
          </DropdownItem>
          <DropdownItem
            key="edit"
            onPress={() => onEditClick?.(data)}
          >
            Edit
          </DropdownItem>
          <DropdownItem
            key="set-active"
            onPress={() => onStatusChange?.(data, 'active')}
            className={data.status === 'active' ? 'text-success' : ''}
          >
            Set Active
          </DropdownItem>
          <DropdownItem
            key="set-inactive"
            onPress={() => onStatusChange?.(data, 'inactive')}
            className={data.status === 'inactive' ? 'text-default-500' : ''}
          >
            Set Inactive
          </DropdownItem>
          <DropdownItem
            key="archive"
            onPress={() => onStatusChange?.(data, 'archived')}
            className={data.status === 'archived' ? 'text-warning' : ''}
          >
            Archive
          </DropdownItem>
          <DropdownItem
            key="delete"
            color="danger"
            startContent={<Trash2 size={16} />}
            description={!canDelete ? 'Used in sessions - archive instead' : undefined}
            onPress={() => onDeleteClick?.(data)}
          >
            Delete
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

/**
 * Objectives Management Screen
 *
 * Two-section layout:
 * - Queued for Next Session (top) - objectives ready for upcoming session
 * - Full Library (bottom) - complete objectives library with AG-Grid table
 */
export const ObjectivesManagement: React.FC = () => {
  // Data state
  const [objectives, setObjectives] = useState<TradingObjective[]>([]);
  const [objectiveTypes, setObjectiveTypes] = useState<ObjectiveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Modal state
  const modalDisclosure = useDisclosure();
  const [editingObjective, setEditingObjective] = useState<TradingObjective | undefined>(undefined);
  const [deletingObjective, setDeletingObjective] = useState<TradingObjective | undefined>(undefined);

  // Toast notifications
  const { showToast } = useToastNotification();

  // ============================================================================
  // Pre-Trading Settings State (maxObjectives + queue with live sync)
  // ============================================================================

  // Local state for maxObjectives (editable)
  const [localMaxObjectives, setLocalMaxObjectives] = useState<number>(3);

  // Local state for queue (editable)
  const [localQueue, setLocalQueue] = useState<QueuedObjective[]>([]);

  // Baseline refs for dirty detection
  const lastSavedMaxRef = useRef<number>(3);
  const lastSavedQueueRef = useRef<string>('[]');

  // Track if initial load has happened (avoids magic number sentinel)
  const hasInitializedRef = useRef(false);

  // Saving state for UI feedback
  const [isSaving, setIsSaving] = useState(false);

  // External update conflict banner state
  const [dismissedExternalUpdate, setDismissedExternalUpdate] = useState(false);

  // Track previous hasQueuedUpdate for transition detection
  const prevHasQueuedUpdateRef = useRef(false);

  // Refs for queue side effects (useEffect-based pattern)
  // Store objectives pending success toast (duplicates handled immediately in handler)
  const pendingQueueActionsRef = useRef<Array<{ objective: TradingObjective }>>([]);

  // Compute dirty state (includes both maxObjectives and queue)
  const isDirty =
    localMaxObjectives !== lastSavedMaxRef.current ||
    JSON.stringify(localQueue) !== lastSavedQueueRef.current;

  // Objective lookup map for O(1) lookups
  const objectiveMap = useMemo(
    () => new Map(objectives.map(o => [o.id, o])),
    [objectives]
  );

  // Derive validQueue - filter out stale entries (deleted objectives)
  // This is the single source of truth for counts, display, and checks
  const validQueue = useMemo(
    () => localQueue.filter(q => objectiveMap.has(q.objectiveId)),
    [localQueue, objectiveMap]
  );

  // Set of valid queued objective IDs for O(1) lookups in renderers
  const queuedIdSet = useMemo(
    () => new Set(validQueue.map(q => q.objectiveId)),
    [validQueue]
  );

  // Transform validQueue to SortableQueueItem[] for SortableList
  // Using flatMap with filter to avoid non-null assertion while maintaining type safety
  const sortableQueueItems: SortableQueueItem[] = useMemo(
    () => validQueue.flatMap(q => {
      const objective = objectiveMap.get(q.objectiveId);
      // Should always exist since validQueue filters, but satisfies ESLint
      return objective ? [{ id: q.objectiveId, queuedObjective: q, objective }] : [];
    }),
    [validQueue, objectiveMap]
  );

  // Handle external updates (show toast)
  const handleExternalUpdate = useCallback(() => {
    showToast({
      title: 'Settings Updated',
      description: 'Objectives settings updated from another tab',
      color: 'success',
    });
  }, [showToast]);

  // Live settings with dirty-state awareness
  const {
    settings: preSettings,
    setSettings: setPreSettings,
    isLoading: preSettingsLoading,
    hasQueuedUpdate,
  } = useLiveSettings<PreTradingSettings>({
    table: 'pre_trading_settings',
    fetchSettings: () => preTradingSettingsService.getCurrentSettings(),
    isDirty,
    onExternalUpdate: handleExternalUpdate,
  });

  // Show conflict banner when external update available while editing
  const showConflictBanner = hasQueuedUpdate && isDirty && !dismissedExternalUpdate;

  // Initialize local state from settings (or defaults)
  // Also handles sync from settings when not dirty
  useEffect(() => {
    if (!preSettings) return;

    const settingsMax = preSettings.maxObjectives;
    const settingsQueue = preSettings.queuedObjectives ?? [];
    const settingsQueueJson = JSON.stringify(settingsQueue);

    // First load: initialize both local state and baselines
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setLocalMaxObjectives(settingsMax);
      lastSavedMaxRef.current = settingsMax;
      setLocalQueue(settingsQueue);
      lastSavedQueueRef.current = settingsQueueJson;
      return;
    }

    // External update while not dirty: sync local state
    if (!isDirty) {
      if (settingsMax !== lastSavedMaxRef.current) {
        setLocalMaxObjectives(settingsMax);
        lastSavedMaxRef.current = settingsMax;
      }
      if (settingsQueueJson !== lastSavedQueueRef.current) {
        setLocalQueue(settingsQueue);
        lastSavedQueueRef.current = settingsQueueJson;
      }
    }
  }, [preSettings, isDirty]);

  // Reset dismissedExternalUpdate when new external update arrives
  useEffect(() => {
    const wasQueued = prevHasQueuedUpdateRef.current;
    const isQueued = hasQueuedUpdate;
    prevHasQueuedUpdateRef.current = hasQueuedUpdate;

    // Transition false→true means new external update arrived
    if (!wasQueued && isQueued) {
      setDismissedExternalUpdate(false);
    }
  }, [hasQueuedUpdate]);

  // Filter state - filters apply immediately as state changes
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set([FILTER_ALL]));
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set([FILTER_ALL]));
  const [tagsFilter, setTagsFilter] = useState<Set<string>>(new Set([FILTER_ALL]));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // View state for collapsible queue and maximizable table
  const [isQueueCollapsed, setIsQueueCollapsed] = useState(false);
  const [isTableMaximized, setIsTableMaximized] = useState(false);

  // Helper to resolve type ID to name
  const getTypeName = useCallback((typeId: string): string => {
    const type = objectiveTypes.find(t => t.id === typeId);
    return type?.name ?? 'Unknown';
  }, [objectiveTypes]);

  // Extract unique tags from all objectives
  const uniqueTags = useMemo(() => {
    const allTags = objectives.flatMap(o => o.tags || []);
    return Array.from(new Set(allTags)).sort();
  }, [objectives]);

  // Filter objectives based on all filter criteria
  const filteredObjectives = useMemo(() => {
    return objectives.filter(objective => {
      // Name search (case-insensitive, name field only)
      const matchesSearch = !searchTerm ||
        objective.name.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter.has(FILTER_ALL) ||
        statusFilter.has(objective.status);

      // Type filter (matches type ID)
      const matchesType = typeFilter.has(FILTER_ALL) ||
        typeFilter.has(objective.type);

      // Tags filter (match any semantics - OR logic)
      const matchesTags = tagsFilter.has(FILTER_ALL) ||
        (objective.tags && objective.tags.some(tag => tagsFilter.has(tag)));

      return matchesSearch && matchesStatus && matchesType && matchesTags;
    });
  }, [objectives, searchTerm, statusFilter, typeFilter, tagsFilter]);

  // Load objectives and types from database
  const loadData = async (): Promise<void> => {
    try {
      setLoading(true);
      const [allObjectives, settings] = await Promise.all([
        tradingObjectiveService.getAll(),
        tradingObjectiveSettingsService.getOrCreateDefaultSettings()
      ]);
      setObjectives(allObjectives);
      setObjectiveTypes(settings.objectiveTypes);
      setLoadError(false);
    } catch (error) {
      console.error('Failed to load objectives:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadData();
  }, []);

  // Subscribe to cross-tab changes for trading_objectives
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('trading_objectives', () => {
      loadData();
    });

    return () => unsubscribe();
  }, []);

  // Modal handlers
  const handleAddClick = () => {
    setEditingObjective(undefined);
    modalDisclosure.onOpen();
  };

  const handleEditClick = useCallback((objective: TradingObjective) => {
    setEditingObjective(objective);
    modalDisclosure.onOpen();
  }, [modalDisclosure]);

  const handleSave = () => {
    loadData();
    modalDisclosure.onClose();
  };

  // Status change handler
  const handleStatusChange = useCallback(async (objective: TradingObjective, newStatus: EntityStatus) => {
    if (objective.status === newStatus) return; // No change needed

    try {
      await tradingObjectiveService.update(objective.id, { status: newStatus });
      // Notify for cross-tab sync (BaseService.update doesn't call notifyChange)
      syncCoordinatorService.notifyChange('trading_objectives', objective.id);
      await loadData(); // Refresh table
    } catch (error) {
      console.error('Failed to update objective status:', error);
    }
  }, []);

  // Delete handlers
  const handleDeleteClick = useCallback((objective: TradingObjective) => {
    setDeletingObjective(objective);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingObjective) return;

    try {
      // 0. Re-fetch objective to guard against race conditions
      // (objective could have been used in a session since modal opened)
      const freshObjective = await tradingObjectiveService.get(deletingObjective.id);
      if (!freshObjective) {
        // Already deleted elsewhere
        setDeletingObjective(undefined);
        await loadData();
        return;
      }
      if ((freshObjective.usageHistory?.length ?? 0) > 0) {
        // Objective was used since modal opened - abort delete
        showToast({
          title: 'Cannot Delete',
          description: 'This objective has been used in sessions. Consider archiving instead.',
          color: 'warning'
        });
        setDeletingObjective(undefined);
        await loadData();
        return;
      }

      // 1. Check if objective is in queue and remove if present
      const settings = await preTradingSettingsService.getCurrentSettings();
      const isQueued = settings.queuedObjectives?.some(q => q.objectiveId === deletingObjective.id);

      if (isQueued && settings.queuedObjectives) {
        const filteredQueue = settings.queuedObjectives.filter(
          q => q.objectiveId !== deletingObjective.id
        );
        await preTradingSettingsService.setQueuedObjectives(filteredQueue);
        // Notify for cross-tab sync of queue changes
        syncCoordinatorService.notifyChange('pre_trading_settings', settings.id);
      }

      // 2. Soft delete the objective (handles its own notifyChange)
      await tradingObjectiveService.softDelete(deletingObjective.id);

      // 3. Refresh table and close modal
      await loadData();
      setDeletingObjective(undefined);
    } catch (error) {
      console.error('Failed to delete objective:', error);
    }
  }, [deletingObjective, showToast]);

  const handleDeleteCancel = useCallback(() => {
    setDeletingObjective(undefined);
  }, []);

  // ============================================================================
  // Queue Handlers
  // ============================================================================

  /**
   * Queue an objective for the next session
   * - Prevents duplicates (check against validQueue, show toast immediately)
   * - Success toasts and limit adjustment handled in useEffect after state commits
   */
  const handleQueueObjective = useCallback((objective: TradingObjective) => {
    // Check if already queued - show toast immediately and return
    if (validQueue.some(q => q.objectiveId === objective.id)) {
      showToast({
        title: 'Already Queued',
        description: `"${objective.name}" is already in the queue`,
        color: 'warning',
      });
      return;
    }

    // Add to pending actions array (for success toasts after state commits)
    pendingQueueActionsRef.current.push({ objective });

    // Functional update for state - only handles state change, no side effects
    setLocalQueue(prev => {
      // Guard: still check prev to handle rapid double-clicks
      if (prev.some(q => q.objectiveId === objective.id)) {
        return prev;
      }

      const newQueuedObjective: QueuedObjective = {
        objectiveId: objective.id,
        isPrimary: false,
      };
      return [...prev, newQueuedObjective];
    });
    // Success toasts and limit adjustment handled in useEffect below
  }, [validQueue, showToast]);

  /**
   * Effect to handle queue side effects after state commits
   * - Shows success toasts based on actual committed state
   * - Adjusts limit if needed based on validQueue (source of truth)
   * Note: Duplicate detection is handled immediately in handleQueueObjective
   */
  useEffect(() => {
    // Return early if no pending actions
    if (pendingQueueActionsRef.current.length === 0) return;

    // Copy and clear pending array atomically
    const pending = [...pendingQueueActionsRef.current];
    pendingQueueActionsRef.current = [];

    // Build set of currently queued IDs from validQueue (source of truth) for O(1) lookups
    const currentQueuedIds = new Set(validQueue.map(q => q.objectiveId));

    // Track if we need to show the limit-increased toast (only once per batch)
    let limitIncreaseShown = false;

    // Process each pending action
    for (const { objective } of pending) {
      // Guard: verify objective actually exists in queue (handles discard/external sync)
      if (!currentQueuedIds.has(objective.id)) {
        // Item was added but then removed (discard/external sync) - silently skip
        continue;
      }

      // Objective was successfully added - check if limit needs adjustment
      if (validQueue.length > localMaxObjectives && !limitIncreaseShown) {
        // Use functional update to safely increase (never lower)
        setLocalMaxObjectives(prev => Math.max(prev, validQueue.length));
        showToast({
          title: 'Objective Queued',
          description: `Limit increased to ${validQueue.length}`,
          color: 'default',
        });
        limitIncreaseShown = true;
      } else {
        showToast({
          title: 'Objective Queued',
          description: `"${objective.name}" added to queue`,
          color: 'success',
        });
      }

      // Show warning for inactive/archived objectives
      if (objective.status !== 'active') {
        showToast({
          title: 'Note',
          description: `This objective is ${objective.status}`,
          color: 'warning',
        });
      }
    }
  }, [validQueue, localMaxObjectives, showToast]);

  /**
   * Remove an objective from the queue
   */
  const handleRemoveFromQueue = useCallback((objectiveId: string) => {
    setLocalQueue(prev => prev.filter(q => q.objectiveId !== objectiveId));
  }, []);

  /**
   * Clear all objectives from the queue
   */
  const handleClearQueue = useCallback(() => {
    setLocalQueue([]);
  }, []);

  /**
   * Handle queue reordering from SortableList
   * Preserves full QueuedObjective data, naturally drops stale entries
   */
  const handleQueueReorder = useCallback((reorderedItems: SortableQueueItem[]) => {
    const reorderedQueue = reorderedItems.map(item => item.queuedObjective);
    setLocalQueue(reorderedQueue);
  }, []);

  // ============================================================================
  // Settings Handlers (maxObjectives + queue)
  // ============================================================================

  /**
   * Handle maxObjectives input change
   */
  const handleMaxObjectivesChange = useCallback((value: number | undefined) => {
    if (value !== undefined && value >= 1) {
      setLocalMaxObjectives(value);
    }
  }, []);

  /**
   * Save settings - persist maxObjectives and queue to service
   * Uses validate-then-persist pattern to avoid partial-save risk
   * Uses validQueue to ensure stale entries are cleaned on save
   */
  const handleSaveSettings = useCallback(async () => {
    if (!preSettings) return;

    setIsSaving(true);
    try {
      // Rebuild queue with correct isPrimary flags (index 0 = primary)
      // Use validQueue to filter out stale entries (deleted objectives)
      const rebuiltQueue: QueuedObjective[] = validQueue.map((q, index) => ({
        ...q,
        isPrimary: index === 0,
      }));

      // Validate queue first (throws on constraint violations, returns warnings)
      const { warnings } = await preTradingSettingsService.validateQueuedObjectives(rebuiltQueue);

      // Single atomic update for both maxObjectives and queue
      const updated = await preTradingSettingsService.update(preSettings.id, {
        ...preSettings,
        maxObjectives: localMaxObjectives,
        queuedObjectives: rebuiltQueue,
      });

      // Update hook state with returned entity (keeps updatedAt in sync)
      setPreSettings(updated);

      // Notify other tabs of the change
      syncCoordinatorService.notifyChange('pre_trading_settings', preSettings.id);

      // Update baseline refs
      lastSavedMaxRef.current = localMaxObjectives;
      lastSavedQueueRef.current = JSON.stringify(rebuiltQueue);

      // Update local queue with rebuilt version (has correct isPrimary)
      setLocalQueue(rebuiltQueue);

      // Reset conflict banner state
      setDismissedExternalUpdate(false);

      // Show warnings if any (consolidated toast)
      if (warnings.length > 0) {
        showToast({
          title: 'Queue saved with warnings',
          description: warnings.join('; '),
          color: 'warning',
        });
      } else {
        showToast({
          title: 'Settings Saved',
          description: 'Objectives settings updated',
          color: 'success',
        });
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      showToast({
        title: 'Save Failed',
        description: errorMessage.includes('validation') ? errorMessage : 'Your changes are preserved. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [preSettings, localMaxObjectives, validQueue, setPreSettings, showToast]);

  /**
   * Discard settings changes - reset both maxObjectives and queue to saved values
   */
  const handleDiscardSettings = useCallback(() => {
    if (!preSettings) return;

    const settingsQueue = preSettings.queuedObjectives ?? [];

    // Reset local state
    setLocalMaxObjectives(preSettings.maxObjectives);
    setLocalQueue(settingsQueue);

    // Update baselines to match current settings
    lastSavedMaxRef.current = preSettings.maxObjectives;
    lastSavedQueueRef.current = JSON.stringify(settingsQueue);

    // Reset conflict banner state
    setDismissedExternalUpdate(false);
  }, [preSettings]);

  /**
   * Accept external changes - reset both local states to settings, clear dirty
   */
  const handleAcceptExternalChanges = useCallback(() => {
    if (!preSettings) return;

    const settingsQueue = preSettings.queuedObjectives ?? [];

    // Reset local state
    setLocalMaxObjectives(preSettings.maxObjectives);
    setLocalQueue(settingsQueue);

    // Update baselines
    lastSavedMaxRef.current = preSettings.maxObjectives;
    lastSavedQueueRef.current = JSON.stringify(settingsQueue);

    setDismissedExternalUpdate(false);
  }, [preSettings]);

  /**
   * Keep editing - dismiss the conflict banner
   */
  const handleKeepEditing = useCallback(() => {
    setDismissedExternalUpdate(true);
  }, []);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter(new Set([FILTER_ALL]));
    setTypeFilter(new Set([FILTER_ALL]));
    setTagsFilter(new Set([FILTER_ALL]));
  };

  // Column definitions
  const columnDefs: ColDef<TradingObjective>[] = useMemo(() => [
    {
      headerName: 'Name',
      field: 'name',
      flex: 1,
      minWidth: 150,
      cellRenderer: NameRenderer,
      cellRendererParams: {
        queuedIdSet,
      },
    },
    {
      headerName: 'Description',
      field: 'description',
      flex: 2,
      minWidth: 200,
      valueFormatter: (params) => params.value || '—'
    },
    {
      headerName: 'Type',
      field: 'type',
      width: 120,
      valueFormatter: (params) => params.value ? getTypeName(params.value) : '—',
      comparator: (valueA: string | undefined, valueB: string | undefined) => {
        const nameA = valueA ? getTypeName(valueA) : '';
        const nameB = valueB ? getTypeName(valueB) : '';
        // Sort unknowns to bottom
        const aUnknown = nameA === 'Unknown' || nameA === '';
        const bUnknown = nameB === 'Unknown' || nameB === '';
        if (aUnknown && !bUnknown) return 1;
        if (!aUnknown && bUnknown) return -1;
        return nameA.localeCompare(nameB);
      }
    },
    {
      headerName: 'Tags',
      field: 'tags',
      width: 180,
      valueFormatter: (params) => params.value?.join(', ') || '',
      cellRenderer: (params: { data?: TradingObjective }) =>
        params.data ? <TagsRenderer data={params.data} /> : null,
      sortable: false
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 100,
      cellRenderer: (params: { data?: TradingObjective }) =>
        params.data ? <StatusRenderer data={params.data} /> : null
    },
    {
      headerName: 'Created',
      field: 'createdAt',
      width: 120,
      valueFormatter: (params) => formatDate(new Date(params.value))
    },
    {
      headerName: 'Actions',
      width: 80,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        onEditClick: handleEditClick,
        onStatusChange: handleStatusChange,
        onDeleteClick: handleDeleteClick,
        onQueueClick: handleQueueObjective,
        isQueueDisabled: loading || loadError,
      },
      sortable: false,
      suppressHeaderMenuButton: true
    }
  ], [handleEditClick, handleStatusChange, handleDeleteClick, handleQueueObjective, getTypeName, queuedIdSet, loading, loadError]);

  // Row class for visual styling based on status
  const getRowClass = useCallback((params: RowClassParams<TradingObjective>): string => {
    if (params.data?.status !== 'active') {
      return 'opacity-50';
    }
    return '';
  }, []);

  return (
    <div className="flex flex-col gap-6 w-full h-full">

      {/* Queued for Next Session Section - hidden when table is maximized */}
      {!isTableMaximized && (
        <Card shadow="none" radius="none">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            {/* Left side: title + chip */}
            <div className="flex items-center gap-2 md:gap-3">
              <h3 className="text-lg font-semibold text-text-primary">
                <span className="md:hidden">Queued</span>
                <span className="hidden md:inline">Queued for Next Session</span>
              </h3>
              <Chip size="sm" variant="flat" color={isDirty ? 'warning' : 'default'}>
                {validQueue.length}/{localMaxObjectives}
              </Chip>
            </div>
            {/* Right side: actions */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Collapsed header: show Save/Discard when dirty */}
              {isQueueCollapsed && isDirty && (
                <>
                  <Button variant="flat" size="sm" onPress={handleDiscardSettings} isDisabled={isSaving}>
                    Discard
                  </Button>
                  <Button
                    color="primary"
                    size="sm"
                    onPress={handleSaveSettings}
                    isDisabled={isSaving || loading || loadError}
                    isLoading={isSaving}
                  >
                    Save
                  </Button>
                </>
              )}
              {/* Expanded header: show Clear Queue and Max Objectives */}
              {!isQueueCollapsed && (
                <>
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    onPress={handleClearQueue}
                    isDisabled={validQueue.length === 0 || preSettingsLoading}
                    className="hidden md:flex"
                  >
                    Clear Queue
                  </Button>
                  <div className="flex flex-col items-end gap-1">
                    <NumberInput
                      label="Max"
                      labelPlacement="outside-left"
                      size="sm"
                      min={1}
                      value={localMaxObjectives}
                      onValueChange={handleMaxObjectivesChange}
                      className="w-24 md:w-48 [&_input]:text-base"
                      isDisabled={preSettingsLoading}
                    />
                    {localMaxObjectives < validQueue.length && (
                      <span className="text-xs text-warning-600 hidden md:block">
                        Queue has {validQueue.length} objectives (exceeds limit)
                      </span>
                    )}
                  </div>
                </>
              )}
              {/* Collapse toggle button */}
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => setIsQueueCollapsed(prev => !prev)}
                aria-label={isQueueCollapsed ? 'Expand queue' : 'Collapse queue'}
              >
                {isQueueCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </Button>
            </div>
          </CardHeader>
          {!isQueueCollapsed && (
            <CardBody>
          {/* External Update Conflict Banner */}
          {showConflictBanner && (
            <div className="rounded-lg bg-warning-50 border border-warning-200 p-4 mb-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                  <span className="text-sm text-warning-700">
                    Objectives settings updated in another tab
                  </span>
                </div>
                <div className="flex flex-col gap-2 md:flex-row">
                  <Button size="sm" variant="flat" onPress={handleKeepEditing}>
                    Keep editing
                  </Button>
                  <Button
                    size="sm"
                    color="warning"
                    onPress={handleAcceptExternalChanges}
                  >
                    Accept changes
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Queued Objectives List */}
          {sortableQueueItems.length > 0 ? (
            <SortableList
              items={sortableQueueItems}
              onReorder={handleQueueReorder}
              className="space-y-2"
              renderItem={(item, dragHandleProps) => (
                <QueuedItemRenderer
                  item={item}
                  dragHandleProps={dragHandleProps}
                  isFirst={item.id === sortableQueueItems[0]?.id}
                  onRemove={handleRemoveFromQueue}
                  getTypeName={getTypeName}
                />
              )}
            />
          ) : (
            <EmptyState
              title="No objectives queued"
              description="Queue objectives from the library below"
              icon={<Target size={24} />}
            />
          )}
          </CardBody>
          )}

          {/* Footer with Save/Discard - only visible when dirty and not collapsed */}
          {isDirty && !isQueueCollapsed && (
            <CardFooter className="flex items-center justify-between gap-4 border-t border-default-200">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-warning-600">Unsaved changes</span>
                {loadError && (
                  <span className="text-xs text-danger-600">Unable to save - objectives failed to load</span>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="flat" onPress={handleDiscardSettings} isDisabled={isSaving}>
                  Discard
                </Button>
                <Button
                  color="primary"
                  onPress={handleSaveSettings}
                  isDisabled={isSaving || loading || loadError}
                  isLoading={isSaving}
                >
                  Save
                </Button>
              </div>
            </CardFooter>
          )}
        </Card>
      )}

      {/* Full Library Section - fills remaining space */}
      <Card shadow="none" radius="none" className="flex flex-col flex-1 min-h-0">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-text-primary">
              <span className="md:hidden">Objectives</span>
              <span className="hidden md:inline">Trading Objectives</span>
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="bordered"
              size="sm"
              isIconOnly
              onPress={() => setIsDrawerOpen(true)}
              className="md:hidden"
              aria-label="Filter"
            >
              <Filter size={16} />
            </Button>
            <Button
              variant="bordered"
              size="sm"
              startContent={<Filter size={16} />}
              onPress={() => setIsDrawerOpen(true)}
              className="hidden md:flex"
            >
              Filter
            </Button>
            <Button
              color="primary"
              size="sm"
              isIconOnly
              onPress={handleAddClick}
              className="md:hidden"
              aria-label="Add Objective"
            >
              <Plus size={16} />
            </Button>
            <Button
              color="primary"
              size="sm"
              startContent={<Plus size={16} />}
              onPress={handleAddClick}
              className="hidden md:flex"
            >
              Add Objective
            </Button>
            <Tooltip content={isTableMaximized ? 'Restore view' : 'Maximize table'}>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => setIsTableMaximized(prev => !prev)}
                aria-label={isTableMaximized ? 'Restore view' : 'Maximize table'}
              >
                {isTableMaximized ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </Button>
            </Tooltip>
          </div>
        </CardHeader>
        <CardBody className="flex-1 min-h-0 p-0">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-text-secondary">Loading objectives...</p>
            </div>
          ) : objectives.length === 0 ? (
            <div className="px-3 pb-3">
              <EmptyState
                title="No objectives yet"
                description="Add your first objective to get started"
                icon={<Target size={24} />}
                action={
                  <Button
                    color="primary"
                    size="sm"
                    startContent={<Plus size={16} />}
                    onPress={handleAddClick}
                  >
                    Add First Objective
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0 px-3 pb-3">
              <AGGridTable<TradingObjective>
                rowData={filteredObjectives}
                columnDefs={columnDefs}
                height="100%"
                defaultColDef={{
                  sortable: true,
                  resizable: true
                }}
                gridOptions={{
                  rowHeight: 48,
                  headerHeight: 40,
                  getRowClass
                }}
              />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Filter Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        placement="right"
        size="sm"
      >
        <DrawerContent>
          <DrawerHeader className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold">Filters</h2>
            <p className="text-small text-foreground-500">Filter your objectives library</p>
          </DrawerHeader>
          <DrawerBody>
            <div className="space-y-6">
              {/* Search Input */}
              <div>
                <Input
                  label="Search"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  startContent={<Search size={18} />}
                  isClearable
                  onClear={() => setSearchTerm('')}
                  className="[&_input]:text-base"
                />
              </div>

              {/* Status Filter */}
              <div>
                <Select
                  label="Status"
                  placeholder="Select statuses"
                  selectionMode="multiple"
                  selectedKeys={statusFilter}
                  onSelectionChange={(keys) => {
                    const newSelection = handleFilterSelection(
                      new Set(Array.from(keys) as string[]),
                      statusFilter
                    );
                    setStatusFilter(newSelection);
                  }}
                >
                  <SelectItem key={FILTER_ALL}>All Statuses</SelectItem>
                  <SelectItem key="active">Active</SelectItem>
                  <SelectItem key="inactive">Inactive</SelectItem>
                  <SelectItem key="archived">Archived</SelectItem>
                </Select>
              </div>

              {/* Type Filter - conditionally rendered */}
              {objectiveTypes.length > 0 && (
                <div>
                  <Select
                    label="Type"
                    placeholder="Select types"
                    selectionMode="multiple"
                    selectedKeys={typeFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(
                        new Set(Array.from(keys) as string[]),
                        typeFilter
                      );
                      setTypeFilter(newSelection);
                    }}
                  >
                    <SelectItem key={FILTER_ALL}>All Types</SelectItem>
                    <>
                      {objectiveTypes.map((type) => (
                        <SelectItem key={type.id}>{type.name}</SelectItem>
                      ))}
                    </>
                  </Select>
                </div>
              )}

              {/* Tags Filter - conditionally rendered */}
              {uniqueTags.length > 0 && (
                <div>
                  <Select
                    label="Tags"
                    placeholder="Select tags"
                    selectionMode="multiple"
                    selectedKeys={tagsFilter}
                    onSelectionChange={(keys) => {
                      const newSelection = handleFilterSelection(
                        new Set(Array.from(keys) as string[]),
                        tagsFilter
                      );
                      setTagsFilter(newSelection);
                    }}
                  >
                    <SelectItem key={FILTER_ALL}>All Tags</SelectItem>
                    <>
                      {uniqueTags.map((tag) => (
                        <SelectItem key={tag}>{tag}</SelectItem>
                      ))}
                    </>
                  </Select>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                <Button
                  variant="flat"
                  onPress={handleClearFilters}
                  className="flex-1"
                >
                  Clear Filters
                </Button>
                <Button
                  color="primary"
                  onPress={() => setIsDrawerOpen(false)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Objective Modal */}
      <ObjectiveModal
        isOpen={modalDisclosure.isOpen}
        onClose={modalDisclosure.onClose}
        onSave={handleSave}
        existingTags={uniqueTags}
        {...(editingObjective && { objective: editingObjective })}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingObjective}
        onClose={handleDeleteCancel}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>
            Delete &quot;{deletingObjective?.name}&quot;?
          </ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              This objective will be removed. This action cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={handleDeleteCancel}>
              Cancel
            </Button>
            <Button color="danger" onPress={handleDeleteConfirm}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};
